import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import {
  ItemType,
  ListingNewWithDetailsDict,
  ListingWithDetails,
  MapMarkerData,
} from "../../../types";
import { ActionSheet, ActionSheetHeightPercent } from "../../ActionSheet";
import { useAppSelector } from "../../../store/hooks";
import { ListingPageLayoutProps } from "../../../types/ui-types";
import axios from "axios";
import { listingItemsToMapMarkers } from "../../../utils/converters";
import { LocationMap } from "../../../store/slices/locationsSlice";
import { CategoryMap, SidebarContent } from "../../Sidebar/SidebarContent";
import { Sidebar } from "../../Sidebar/Sidebar";
import Map from "../../Map";
import useMediaQuery, { MediaMap } from "../../../hooks/useDimensions";
import {
  selectListingsWithDetails,
  selectConnections,
} from "../../../store/slices/listingsSlice";
import ListingForm from "../../Form/ListingForm";

const VALID_TABS: ItemType[] = [
  "person",
  "event",
  "product",
  "project",
  "service",
  "creative",
  "organization",
];

export const LayoutNetwork = ({
  listing: network,
  location,
  tabs,
  hasContactForm = false,
}: ListingPageLayoutProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { verifyCode, refreshUser } = useAuth();
  const verifyingCodeRef = useRef<string | null>(null);

  // STATE
  const [selectedItem, setSelectedItem] = useState<ListingWithDetails | null>(
    null
  );
  const [showingOfferingForm, setShowingOfferingForm] =
    useState<ItemType | null>(null);
  const [hoveredItem, setHoveredItem] = useState<ListingWithDetails | null>(
    null
  );
  const [actionSheetHeight, setActionSheetHeight] =
    useState<ActionSheetHeightPercent>(50);

  // Track if we've set the initial default tab
  const hasSetDefaultTab = useRef(false);

  // Read URL params and derive activeTab
  const urlTab = searchParams.get("tab") as ItemType | null;
  const activeTab: ItemType =
    urlTab && VALID_TABS.includes(urlTab) ? urlTab : "person";

  // Set default tab in URL ONLY on initial mount if not present
  useEffect(() => {
    if (
      !hasSetDefaultTab.current &&
      (!urlTab || !VALID_TABS.includes(urlTab))
    ) {
      hasSetDefaultTab.current = true;
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("tab", "person");
          return params;
        },
        { replace: true }
      );
    }
  }, [urlTab, setSearchParams]);

  // Read other URL params
  const showMemberForm = searchParams.get("form") === "person";
  const prefilledEmail = searchParams.get("email") || undefined;
  const fromInvitation = searchParams.get("fromInvitation") === "true";
  const urlSelectedId = searchParams.get("selected");
  const magicCode = searchParams.get("code");

  // Auto-verify magic code from URL
  useEffect(() => {
    const verifyMagicCode = async () => {
      // Prevent double-execution (React StrictMode in dev runs effects twice)
      if (magicCode && verifyingCodeRef.current !== magicCode) {
        verifyingCodeRef.current = magicCode;

        try {
          await verifyCode(magicCode);
          await refreshUser();

          // Remove code from URL after successful verification
          setSearchParams(
            (prev) => {
              const params = new URLSearchParams(prev);
              params.delete("code");
              return params;
            },
            { replace: true }
          );
          alert(
            "You're now logged in! You can create offerings and manage your profile."
          );
        } catch (error: any) {
          const errorMessage =
            error.response?.data?.error ||
            error.message ||
            "Invalid or expired code";
          alert(`Login failed: ${errorMessage}`);
          // Remove invalid code from URL
          setSearchParams(
            (prev) => {
              const params = new URLSearchParams(prev);
              params.delete("code");
              return params;
            },
            { replace: true }
          );
        }
      }
    };

    verifyMagicCode();
  }, [magicCode, verifyCode, refreshUser, setSearchParams]);

  // SELECTORS
  // Redux state
  const locations = useAppSelector((state) => state.locations?.byId);
  const allListings: ListingNewWithDetailsDict = useAppSelector(
    selectListingsWithDetails
  );
  const connections = useAppSelector(selectConnections);
  const listingsLoading = useAppSelector((state) => state.listings.loading);

  // Filter listings to only show those with fully-approved 'listed' connections to the network
  const listings: ListingNewWithDetailsDict = useMemo(() => {
    if (!network?.id || !connections) return allListings;

    // Find all listing IDs that have a fully-approved 'listed' connection to the network
    const approvedListingIds = new Set<string>();

    connections.forEach((conn) => {
      // Check if this is a 'listed' connection to the network
      if (
        conn.objectListingId === network.id &&
        conn.roleType === "listed" &&
        conn.subjectApprovalStatus === "approved" &&
        conn.objectApprovalStatus === "approved"
      ) {
        approvedListingIds.add(conn.subjectListingId);
      }
    });

    // Always include the network itself
    approvedListingIds.add(network.id);

    // Filter listings to only those that are approved network members
    const filtered: ListingNewWithDetailsDict = {};
    Object.entries(allListings).forEach(([id, listing]) => {
      if (approvedListingIds.has(id)) {
        filtered[id] = listing;
      }
    });

    return filtered;
  }, [allListings, connections, network?.id]);

  // HOOKS
  const mediaMap = useMediaQuery();

  // VARS
  const mapWidthPercent = getMapWidthPercent(mediaMap); // % width for map component

  // FUNCTIONS
  // URL-first navigation: atomic updates to avoid racing setSearchParams calls
  const handleItemClick = useCallback(
    (item: ListingWithDetails | null) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (item) {
            params.set("tab", item.itemType);
            params.set("selected", item.id);
          } else {
            params.delete("selected");
          }
          return params;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  const handleTabChange = useCallback(
    (tab: ItemType) => {
      // Atomic update: set tab AND clear selection in one call
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("tab", tab);
          params.delete("selected");
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const handleListingSubmit = async (listingData: any) => {
    try {
      // Add this network to the 'listed' array so the listing gets associated with it
      const listedArray = [{ id: network.id }];
      listingData.append("listed", JSON.stringify(listedArray));

      const response = await axios.post("/api/listing", listingData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      });

      // If this is a new member signup, create user account and send magic link
      const isMemberSignup = listingData.get("itemType") === "person";
      const email = listingData.get("email");
      const name = listingData.get("name");

      if (isMemberSignup && email && name && response.data.listingId) {
        try {
          // Create user account with the listing ID as their primary listing
          await axios.post(
            "/api/auth/signup",
            {
              email,
              name,
              primaryListingId: response.data.listingId,
            },
            { withCredentials: true }
          );

          navigate({ pathname: "/", search: "" }, { replace: true });
          const offeringName = searchParams.get("offering");
          alert(
            fromInvitation
              ? `Thank you for joining! Check your email for a magic login link. After logging in, you'll be linked to "${offeringName}".`
              : "Thank you for joining! Check your email for a magic login link to access your account."
          );
        } catch (signupErr: any) {
          // If user already exists, that's okay - listing was still created
          if (signupErr.response?.data?.error?.includes("already exists")) {
            navigate({ pathname: "/", search: "" }, { replace: true });
            alert(
              "Profile submitted! You can log in with your existing account."
            );
          } else {
            console.error("Signup error:", signupErr);
            navigate({ pathname: "/", search: "" }, { replace: true });
            alert(
              "Profile submitted! However, we couldn't create your account. Please contact support."
            );
          }
        }
      } else if (isMemberSignup) {
        // Member submission without signup (shouldn't happen normally)
        navigate({ pathname: "/", search: "" }, { replace: true });
        alert(
          "New member submitted successfully! It will appear on the map after approval."
        );
      } else {
        // Offering submission
        setShowingOfferingForm(null);
        alert(
          "Listing submitted successfully! It will appear on the map after approval."
        );
        await refreshUser(); // Refresh to get updated listing count
      }

      return response.data;
    } catch (err) {
      console.error("Error submitting listing:", err);
      alert("Failed to submit listing. Please try again.");
      throw err;
    }
  };

  const handleJoinNetwork = () => {
    navigate("/?form=member");
  };

  const handleCloseMemberForm = () => {
    navigate({ pathname: "/", search: "" }, { replace: true });
  };

  // MEMOS
  const mapMarkersFromListings: MapMarkerData[] = useMemo(() => {
    if (!listings || !locations) return [];
    return listingItemsToMapMarkers(Object.values(listings), locations);
  }, [listings, locations]);

  const members = useMemo(() => {
    return Object.values(listings).filter((l) => l.itemType === "person");
  }, [listings]);

  const locationsByGoogleId = useMemo(() => {
    const byPlaceId: LocationMap = {};
    Object.values(locations).forEach((location) => {
      if (location.placeId) {
        byPlaceId[location.placeId] = location;
      }
    });

    return byPlaceId;
  }, [locations]);

  const categories: CategoryMap = useMemo(() => {
    if (!listings) return {} as CategoryMap;
    return getCategoriesForSearch(Object.values(listings));
  }, [listings]);

  // Sync URL params with state (must be after listingItems is defined)
  useEffect(() => {
    // Sync selected item from URL
    if (urlSelectedId && listings[urlSelectedId]) {
      const item = listings[urlSelectedId];
      if (item !== selectedItem) {
        setSelectedItem(item);
      }
    } else if (!urlSelectedId && selectedItem) {
      setSelectedItem(null);
    }
  }, [urlSelectedId, listings, selectedItem]);

  return (
    <>
      <div className="main-content">
        {/* DESKTOP SIDEBAR */}
        <div
          className="desktop-sidebar"
          style={{ height: "100%", width: `${100 - mapWidthPercent}%` }}
        >
          <Sidebar>
            <SidebarContent
              activeTab={activeTab}
              listingItemDict={listings}
              setActiveTab={handleTabChange}
              categories={categories}
              setShowJoinForm={handleJoinNetwork}
              setShowingOfferingForm={setShowingOfferingForm}
              locations={locations}
              selectedItem={selectedItem}
              setSelectedItem={handleItemClick}
              loading={listingsLoading}
            />
          </Sidebar>
        </div>

        {/* MAP */}
        <div
          style={{
            height: "100%",
            width: `${mapWidthPercent}%`,
          }}
        >
          <Map
            mapMarkers={mapMarkersFromListings}
            listingItemsDict={listings}
            selectedItem={selectedItem}
            hoveredItem={hoveredItem}
            onItemHover={setHoveredItem}
            onItemSelect={handleItemClick}
            locations={locations}
          />
        </div>

        {/* Mobile Action Sheet */}
        <div className="mobile-actionsheet">
          <ActionSheet
            heightPercent={actionSheetHeight}
            setHeightPercent={setActionSheetHeight}
          >
            <SidebarContent
              activeTab={activeTab}
              listingItemDict={listings}
              setActiveTab={handleTabChange}
              categories={categories}
              setShowJoinForm={handleJoinNetwork}
              setShowingOfferingForm={setShowingOfferingForm}
              locations={locations}
              selectedItem={selectedItem}
              setSelectedItem={handleItemClick}
              loading={listingsLoading}
            />
          </ActionSheet>
        </div>
      </div>

      {showingOfferingForm && showingOfferingForm !== "person" && (
        <ListingForm
          onSubmit={handleListingSubmit}
          onClose={() => setShowingOfferingForm(null)}
          itemType={showingOfferingForm}
          members={members}
          existingLocations={locationsByGoogleId}
        />
      )}

      {showMemberForm && (
        <ListingForm
          onSubmit={handleListingSubmit}
          onClose={handleCloseMemberForm}
          itemType="person"
          members={members}
          existingLocations={locationsByGoogleId}
          prefilledEmail={prefilledEmail}
          fromInvitation={fromInvitation}
        />
      )}
    </>
  );
};

function getMapWidthPercent(mediaMap: MediaMap): number {
  if (mediaMap.isMobile) return 100;
  if (mediaMap.isTablet) return 50;
  return 44;
}

function getCategoriesForSearch(listings: ListingWithDetails[]): CategoryMap {
  const categories: CategoryMap = {
    event: [],
    product: [],
    project: [],
    service: [],
    person: [],
    creative: [],
    organization: [],
    article: [],
    course: [],
  };

  listings.forEach((offering) => {
    const type = offering.itemType || "";
    const subtype: string = offering.subType || "";

    if (!type || !subtype) return;

    const shouldAdd = !categories[type]?.includes(subtype);

    if (shouldAdd && categories[offering.itemType]) {
      categories[offering.itemType].push(offering?.subType || "");
    }
  });

  return categories;
}
